import {
  CAMPAIGN_BODY_EDITABLE_PROPERTIES,
  CAMPAIGN_INBOX_EDITABLE_PROPERTIES,
  LEGACY_DESIGN_BODY_PLACEHOLDER
} from 'features/EmailEditor/constants'
import { getTemplate, resolveEditorTemplateKey } from 'features/EmailEditor/templates'
import {
  extractPreviewBodyHtml,
  extractSocialMediaUrls,
  generateSocialMediaLinksHtml,
  getHtmlFromMjml,
  mergeNotificationEmailPayload,
  normalizeEmailFooterFields,
  prepareEditorialEmailHtml,
  replaceTagsSyntax,
  resolveSocialFooterTheme,
  resolveStoredShellTemplateKey
} from 'features/EmailEditor/utils'
import {
  normalizeRuntimePlaceholderLinksInHtml,
  normalizeRuntimePlaceholderLinksInMarkdown
} from './inviteEmailTemplateBodyInjector'
import { marked } from 'marked'
import inviteEmailDesignApi from 'services/api/inviteEmailDesign'

export const CAMPAIGN_EMAIL_CUSTOMIZATION_PROPERTIES = [
  ...CAMPAIGN_INBOX_EDITABLE_PROPERTIES,
  ...CAMPAIGN_BODY_EDITABLE_PROPERTIES
]

const GLOBAL_EMAIL_PARAM_KEYS = new Set([
  'termsUrl',
  'privacyPolicyUrl',
  'hotelName',
  'hotelAddress',
  'hotelCityStateZip',
  'hotelPhone',
  'hotelWebsite',
  'hotelWebsiteUrl',
  'socialMediaLinks'
])

export function extractPreservedCampaignBodyFields(emailParams = {}) {
  const richtextFields = normalizeCampaignRichtextFields(emailParams.richtextFields || {})

  return {
    subject: emailParams.subject ?? emailParams.Subject,
    preheaderText: emailParams.preheaderText ?? emailParams.PreheaderText,
    emailTitle: emailParams.emailTitle ?? emailParams.EmailTitle,
    bodyHtml: richtextFields.bodyHtml,
    ctaText: emailParams.ctaText ?? emailParams.CtaText
  }
}

export function normalizeCampaignRichtextFields(richtextFields = {}) {
  return {
    bodyHtml: richtextFields.bodyHtml ?? richtextFields.BodyHtml ?? '',
    col1Html: richtextFields.col1Html ?? richtextFields.Col1Html ?? '',
    col2Html: richtextFields.col2Html ?? richtextFields.Col2Html ?? '',
    col3Html: richtextFields.col3Html ?? richtextFields.Col3Html ?? '',
    personalHtml: richtextFields.personalHtml ?? richtextFields.PersonalHtml ?? '',
    finePrintHtml: richtextFields.finePrintHtml ?? richtextFields.FinePrintHtml ?? ''
  }
}

export function normalizeCampaignEmailParams(emailParams = {}) {
  const source = emailParams && typeof emailParams === 'object' ? emailParams : {}
  const richtextFields = source.richtextFields ?? source.RichtextFields ?? {}

  return {
    ...source,
    shellTemplateKey: source.shellTemplateKey ?? source.ShellTemplateKey,
    selectedTemplate: source.selectedTemplate ?? source.SelectedTemplate,
    emailTitle: source.emailTitle ?? source.EmailTitle,
    subject: source.subject ?? source.Subject ?? '',
    preheaderText: source.preheaderText ?? source.PreheaderText ?? '',
    ctaText: source.ctaText ?? source.CtaText,
    subsidiaryEmailTemplateId:
      source.subsidiaryEmailTemplateId ?? source.SubsidiaryEmailTemplateId,
    richtextFields: normalizeCampaignRichtextFields(richtextFields)
  }
}

export function normalizeCampaignEmailResponse(data = {}) {
  if (!data || typeof data !== 'object') {
    return {
      html: '',
      emailParams: {},
      globalParams: {}
    }
  }

  return {
    html: data.html ?? data.Html ?? '',
    emailParams: normalizeCampaignEmailParams(data.emailParams ?? data.EmailParams ?? {}),
    globalParams: data.globalParams ?? data.GlobalParams ?? {}
  }
}

export function mergeDesignTemplateWithCampaignBody(
  designData,
  currentEmailParams = {},
  { brandLogoUrl } = {}
) {
  const shellTemplateKey =
    designData?.shellTemplateKey || resolveStoredShellTemplateKey(designData?.emailParams)
  const preserved = extractPreservedCampaignBodyFields(currentEmailParams)
  const themeDefault = getTemplate(resolveEditorTemplateKey(shellTemplateKey)).params

  const merged = mergeNotificationEmailPayload(
    themeDefault,
    designData?.emailParams,
    designData?.globalParams,
    { brandLogoUrl }
  )

  merged.shellTemplateKey = shellTemplateKey
  merged.selectedTemplate = resolveEditorTemplateKey(shellTemplateKey)

  if (preserved.subject) {
    merged.subject = preserved.subject
  }

  if (preserved.preheaderText) {
    merged.preheaderText = preserved.preheaderText
  }

  if (preserved.emailTitle) {
    merged.emailTitle = preserved.emailTitle
  }

  if (preserved.ctaText) {
    merged.ctaText = preserved.ctaText
  }

  merged.richtextFields = {
    ...(merged.richtextFields || {}),
    ...(preserved.bodyHtml ? { bodyHtml: preserved.bodyHtml } : {})
  }

  return merged
}

export function stripGlobalEmailParams(emailState = {}) {
  return Object.entries(emailState).reduce((acc, [key, value]) => {
    if (!GLOBAL_EMAIL_PARAM_KEYS.has(key)) {
      acc[key] = value
    }
    return acc
  }, {})
}

export function resolveStoredDesignTemplateId(emailParams = {}) {
  const rawId = emailParams?.subsidiaryEmailTemplateId ?? emailParams?.SubsidiaryEmailTemplateId
  const parsed = Number(rawId)

  return Number.isFinite(parsed) ? parsed : null
}

export function resolveDesignBodyHtml(storedBody, templateDefaultBody) {
  const trimmed = `${storedBody ?? ''}`.trim()

  if (!trimmed || trimmed === LEGACY_DESIGN_BODY_PLACEHOLDER) {
    const fallback = `${templateDefaultBody ?? ''}`.trim()
    return fallback || ''
  }

  return storedBody
}

export function resolveDesignStoryRichtextField(storedValue, templateDefault) {
  const stored = `${storedValue ?? ''}`.trim()

  if (stored) {
    return storedValue
  }

  const fallback = `${templateDefault ?? ''}`.trim()
  return fallback || ''
}

export function mergeDesignRichtextFields(templateParams = {}, storedRichtextFields = {}) {
  const templateDefaults = templateParams.richtextFields || {}
  const normalizedStored = normalizeCampaignRichtextFields(storedRichtextFields)

  return {
    bodyHtml: resolveDesignBodyHtml(normalizedStored.bodyHtml, templateDefaults.bodyHtml),
    col1Html: resolveDesignStoryRichtextField(normalizedStored.col1Html, templateDefaults.col1Html),
    col2Html: resolveDesignStoryRichtextField(normalizedStored.col2Html, templateDefaults.col2Html),
    col3Html: resolveDesignStoryRichtextField(normalizedStored.col3Html, templateDefaults.col3Html),
    personalHtml: resolveDesignStoryRichtextField(
      normalizedStored.personalHtml,
      templateDefaults.personalHtml
    ),
    finePrintHtml: resolveDesignStoryRichtextField(
      normalizedStored.finePrintHtml,
      templateDefaults.finePrintHtml
    )
  }
}

export function resolveEditorRichtextFields(templateParams = {}, storedRichtextFields = {}) {
  return mergeDesignRichtextFields(templateParams, storedRichtextFields)
}

export function mergeTemplateSwitchRichtextFields(templateParams = {}, previousRichtextFields = {}) {
  const templateDefaults = templateParams.richtextFields || {}

  return {
    ...templateDefaults,
    bodyHtml: resolveDesignBodyHtml(previousRichtextFields.bodyHtml, templateDefaults.bodyHtml)
  }
}

export function buildRichtextHtmlMap(richtextFields = {}) {
  return Object.keys(richtextFields).reduce((acc, key) => {
    if (!richtextFields[key]) {
      acc[key] = ''
      return acc
    }

    const normalizedMarkdown = normalizeRuntimePlaceholderLinksInMarkdown(richtextFields[key])
    acc[key] = normalizeRuntimePlaceholderLinksInHtml(
      marked.parse(replaceTagsSyntax(normalizedMarkdown))
    )
    return acc
  }, {})
}

export function renderCustomizationBodyHtml(bodyMarkdown) {
  if (!bodyMarkdown) {
    return ''
  }

  return marked.parse(replaceTagsSyntax(bodyMarkdown))
}

export async function resolveCampaignBodyShellHtml(emailData) {
  const templateId = resolveStoredDesignTemplateId(emailData?.emailParams)

  if (templateId) {
    try {
      const designData = await inviteEmailDesignApi.getTemplate({ templateId })

      if (designData?.html) {
        return designData.html
      }
    } catch {
      // Fall back to stored campaign html when the design template cannot be loaded.
    }
  }

  return emailData?.html || null
}

export function resolveDesignTemplateForCampaign(templates, emailParams = {}) {
  if (!templates?.length) {
    return null
  }

  const templateId = resolveStoredDesignTemplateId(emailParams)
  if (!templateId) {
    return null
  }

  return templates.find(template => template.id === templateId) || null
}

export function hasCampaignEmailContent(emailData) {
  return Boolean(
    emailData?.html ||
      emailData?.emailParams?.subject ||
      emailData?.emailParams?.emailTitle ||
      emailData?.emailParams?.richtextFields?.bodyHtml
  )
}

export function extractPreviewHtmlBody(html) {
  return extractPreviewBodyHtml(html)
}

export function buildCampaignEmailHtml(emailState, shellTemplateKey) {
  const resolvedKey = resolveEditorTemplateKey(shellTemplateKey)
  const currentTemplate = getTemplate(resolvedKey)
  const socialUrls = extractSocialMediaUrls(emailState.socialMediaLinks)
  const templateVariables = normalizeEmailFooterFields({
    ...emailState,
    ...socialUrls
  })
  const socialTheme = resolveSocialFooterTheme(resolvedKey)

  return prepareEditorialEmailHtml(
    getHtmlFromMjml(
      currentTemplate.template,
      templateVariables,
      {
        ...Object.keys(emailState.richtextFields || {}).reduce((acc, key) => {
          acc[key] = emailState.richtextFields[key]
            ? marked.parse(replaceTagsSyntax(emailState.richtextFields[key]))
            : ''
          return acc
        }, {}),
        socialMediaLinksHtml: generateSocialMediaLinksHtml(
          emailState.socialMediaLinks,
          socialTheme.theme,
          socialTheme.brandColor
        )
      },
      { templateKey: resolvedKey }
    ),
    resolvedKey,
    { ...emailState, ...templateVariables }
  )
}
